import { Injectable } from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// multi-asset
////////////////////////////////
import { ATDLUtilityService } from "./atdl-utility.service";
///
import { ATDLDataModel } from "../../data-atdl/model/atdl-data.model";
// interface models
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
// ENUMS
import { ICAutocompleteGenericModel } from "../../interface/controls/models/bfx-interface-controls.models";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { NullProtoObject } from "../../../../shared/helper-services/null-proto-object";

@Injectable()
export class ATDLDatasetService {
  /**********************************************************************/
  /* DATA  */
  private dataset: NullProtoObject<ATDLDataModel> = Object.create(null);
  private internalAlgos: NullProtoObject<ATDLDataModel> = Object.create(null);

  private invalidAlgos = new Array<string>();

  private tradableAlgos: Set<string> = new Set();

  private _isPrimed: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    protected atdlUtility: ATDLUtilityService,
    activeUserService: BfxUserProfileService,
  ) {
    activeUserService.getUserData().then((user) => {
      if (user && user.tradableAlgos) {
        this.tradableAlgos = user.tradableAlgos;
      }
    });
  }

  /**********************************************************************/

  /**
   * Explicitly set the data source as primed - required in cases where there are no ATDL definitions at all
   */
  public setPrimed(): void {
    this._isPrimed = true;
  }

  /* ALGO DATASET METHODS */
  public isPrimed(): boolean {
    return this._isPrimed;
  }

  public add(algoId: string, model: ATDLDataModel): void {
    if (typeof algoId === "string" && algoId.length > 0 && model instanceof ATDLDataModel) {
      if (!Object.hasOwn(this.dataset, algoId))
        this.dataset = Object.assign(Object.create(null), this.dataset);

      this.dataset[algoId] = model;
    } else {
      logger.out.error(`ERROR: atdlParser.add(): Invalid Id or model!`);
    }
  }

  /**
   * Internal algos can be retreived if their name is known using get but
   * are not returned in any listing - uised for  algos defined in the ATDL designer
   * @param algoId
   * @param model
   */
  public addInternal(algoId: string, model: ATDLDataModel): void {
    if (typeof algoId === "string" && algoId.length > 0 && model instanceof ATDLDataModel) {
      if (!Object.hasOwn(this.internalAlgos, algoId))
        this.internalAlgos = Object.assign(Object.create(null), this.internalAlgos);

      this.internalAlgos[algoId] = model;
    } else {
      logger.out.error(`ERROR: atdlParser.addInternal(): Invalid Id or model!`);
    }
  }

  public deleteInternal(algoId: string): void {
    if (typeof algoId === "string" && Object.hasOwn(this.internalAlgos, algoId)) {
      delete this.internalAlgos[algoId];
      logger.out.warn(`WARNING: atdlParser.delete(): ${algoId} removed from the algo dataset!`);
    } else {
      logger.out.error(`ERROR: atdlParser.delete(): Invalid Algo Id!`);
    }
  }

  public delete(algoId: string): void {
    if (typeof algoId === "string" && Object.hasOwn(this.dataset, algoId)) {
      delete this.dataset[algoId];
      logger.out.warn(`WARNING: atdlParser.delete(): ${algoId} removed from the algo dataset!`);
    } else {
      logger.out.error(`ERROR: atdlParser.delete(): Invalid Algo Id!`);
    }
  }

  public has(algoId: string): boolean {
    return (
      typeof algoId === "string" &&
      ((Object.hasOwn(this.dataset, algoId) && this.dataset[algoId] instanceof ATDLDataModel) ||
        (Object.hasOwn(this.internalAlgos, algoId) &&
          this.internalAlgos[algoId] instanceof ATDLDataModel))
    );
  }

  public reset(): void {
    this.dataset = Object.create(null);
  }

  /**********************************************************************/
  /* INVALID ALGOs RECORD */
  public recordInvalidAlgo(algoId: string): void {
    this.invalidAlgos.push(algoId);
  }

  public getInvalidAlgoIds(): Array<string> {
    return this.invalidAlgos;
  }

  public getLimitPriceMode(algoId: string): ICDNAuxMode {
    if (typeof algoId === "string" && Object.hasOwn(this.dataset, algoId)) {
      return this.dataset[algoId].limitPriceMode;
    } else {
      return ICDNAuxMode.DISABLED;
    }
  }

  public isEmpty(): boolean {
    return this.dataset == null || Object.keys(this.dataset).length == 0;
  }

  public getAll(): NullProtoObject<ATDLDataModel> {
    return this.isPrimed()
      ? this.atdlUtility.clone<NullProtoObject<ATDLDataModel>>(this.dataset)
      : Object.create(null);
  }

  public getAllAsArray(): Array<ATDLDataModel> {
    return this.isPrimed() ? Object.values(this.dataset) : [];
  }

  public get(algoId: string): ATDLDataModel {
    if (this.has(algoId)) {
      const algo = this.dataset[algoId] ?? this.internalAlgos[algoId];
      return this.atdlUtility.clone(algo);
    }
    return null;
  }

  /**
   * Get array of Autocomplete models for all defined algos in the system, regardless of tradability
   * @returns
   */
  public getAllAlgosForAutoComplete(): ICAutocompleteGenericModel<string>[] {
    return this.getAlgosForAutoComplete(true);
  }

  /**
   * Get array of Autocomplete models for  tradable algos in the system only
   * @returns
   */
  public getTradableAlgosForAutoComplete(): ICAutocompleteGenericModel<string>[] {
    return this.getAlgosForAutoComplete(false);
  }

  /**
   * Build up a sorted list of autocomplete models for algos
   * @param includeAll Return all algos? If false, then only tradable algos are returned
   * @returns
   */
  private getAlgosForAutoComplete(includeAll: boolean): ICAutocompleteGenericModel<string>[] {
    let result: Array<ICAutocompleteGenericModel<string>> = [];
    if (Object.keys(this.dataset).length > 0) {
      for (let item of Object.values(this.dataset)) {
        if (item instanceof ATDLDataModel && (includeAll || this.tradableAlgos.has(item.id))) {
          let itemObject = new ICAutocompleteGenericModel(item.id, item.description);
          result.push(itemObject);
        }
      }

      if (result.length > 1) {
        result.sort((a: ICAutocompleteGenericModel, b: ICAutocompleteGenericModel) =>
          a.label.localeCompare(b.label),
        );
      }
    }
    return result;
  }

  /**********************************************************************/
  /* ATDL MODEL - Interface Control */

  public doesICDExist(algoId: string, icdId: string): boolean {
    return this.getICDModel(algoId, icdId) instanceof ATDLInterfaceControlModel;
  }

  public getICDRefName(algoId: string, icdId: string): string {
    if (this.has(algoId) && typeof icdId === "string") {
      let model = this.get(algoId);
      let targetControl: ATDLInterfaceControlModel = model.controls.find(
        (item) => item.icdId === icdId,
      );
      return targetControl instanceof ATDLInterfaceControlModel ? targetControl.refName : null;
    }
    return null;
  }

  public getICDModel(algoId: string, icdId: string): ATDLInterfaceControlModel {
    if (this.has(algoId) && typeof icdId === "string") {
      let model = this.get(algoId);
      let targetControl: ATDLInterfaceControlModel = model.controls.find(
        (item) => item.icdId === icdId,
      );
      return targetControl instanceof ATDLInterfaceControlModel
        ? this.atdlUtility.clone(targetControl)
        : null;
    }
    return null;
  }
}
